import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import {
  LogOut, Save, Upload, Trash2, Image as ImageIcon, Settings as SettingsIcon, Inbox, CheckCircle2,
} from "lucide-react";

const TABS = [
  { key: "settings", label: "Business Info", icon: SettingsIcon },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
  { key: "inquiries", label: "Inquiries", icon: Inbox },
];

export default function AdminDashboard({ onSettingsSaved }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("settings");

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#0A0B0E]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">/// Admin Console</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl mt-3">
              Control <span className="text-[#00A896]">Room.</span>
            </h1>
            <p className="text-[#8C939D] mt-2 text-sm">Signed in as <span className="text-white font-mono">{user?.email}</span></p>
          </div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="btn-outline text-xs"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-10 border-b border-[#282C33] flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] flex items-center gap-2 border-b-2 transition-colors ${
                tab === t.key ? "border-[#00A896] text-[#00A896]" : "border-transparent text-[#8C939D] hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "settings" && <SettingsTab onSaved={onSettingsSaved} />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "inquiries" && <InquiriesTab />}
        </div>
      </div>
    </div>
  );
}

/* ---------- Settings Tab ---------- */
function SettingsTab({ onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ ok: false, error: "" });

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, logo_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg({ ok: false, error: "" });
    try {
      await api.put("/admin/settings", form);
      setMsg({ ok: true, error: "" });
      if (onSaved) onSaved();
      setTimeout(() => setMsg({ ok: false, error: "" }), 2500);
    } catch (err) {
      setMsg({ ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="overline text-[#8C939D]">Loading…</div>;

  return (
    <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="admin-settings-form">
      <div className="lg:col-span-8 brut-card p-6 lg:p-8 space-y-5">
        <div className="overline">/// Public Info</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Business Name" testid="business-name">
            <input className="input-brut" value={form.business_name || ""} onChange={update("business_name")} data-testid="admin-business-name-input" />
          </Field>
          <Field label="Tagline" testid="tagline">
            <input className="input-brut" value={form.tagline || ""} onChange={update("tagline")} data-testid="admin-tagline-input" />
          </Field>
          <Field label="Mobile *" testid="mobile">
            <input className="input-brut" value={form.mobile || ""} onChange={update("mobile")} data-testid="admin-mobile-input" />
          </Field>
          <Field label="WhatsApp" testid="whatsapp">
            <input className="input-brut" value={form.whatsapp || ""} onChange={update("whatsapp")} data-testid="admin-whatsapp-input" />
          </Field>
          <Field label="Email *" testid="email">
            <input type="email" className="input-brut" value={form.email || ""} onChange={update("email")} data-testid="admin-email-input" />
          </Field>
          <Field label="Map Embed URL (optional)" testid="map">
            <input className="input-brut" placeholder="Auto-generated from address if blank" value={form.map_embed_url || ""} onChange={update("map_embed_url")} data-testid="admin-map-input" />
          </Field>
        </div>

        <Field label="Address *" testid="address">
          <textarea rows={3} className="input-brut resize-none" value={form.address || ""} onChange={update("address")} data-testid="admin-address-input" />
        </Field>

        <Field label="About" testid="about">
          <textarea rows={5} className="input-brut resize-none" value={form.about || ""} onChange={update("about")} data-testid="admin-about-input" />
        </Field>
      </div>

      <div className="lg:col-span-4 space-y-4">
        <div className="brut-card p-6">
          <div className="overline">/// Logo</div>
          <div className="mt-4 aspect-square bg-[#0A0B0E] border border-[#282C33] flex items-center justify-center overflow-hidden">
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <span className="text-[#8C939D] text-xs font-mono uppercase">No logo</span>
            )}
          </div>
          <label className="btn-outline text-xs mt-4 w-full justify-center cursor-pointer" data-testid="admin-logo-upload-btn">
            <Upload size={14} /> Upload Logo
            <input type="file" accept="image/*" className="hidden" onChange={onLogo} />
          </label>
          {form.logo_url && (
            <button type="button" onClick={() => setForm((s) => ({ ...s, logo_url: "" }))} className="text-xs font-mono uppercase tracking-[0.2em] text-[#DC2626] mt-3 hover:underline">
              Remove logo
            </button>
          )}
        </div>

        <button type="submit" disabled={saving} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-settings-save-btn">
          {saving ? "Saving…" : <><Save size={16} /> Save changes</>}
        </button>

        {msg.ok && (
          <div className="border border-[#16A34A] text-[#86EFAC] p-3 text-sm flex items-center gap-2" data-testid="admin-settings-success">
            <CheckCircle2 size={16} /> Saved successfully
          </div>
        )}
        {msg.error && (
          <div className="border border-[#DC2626] text-[#FCA5A5] p-3 text-sm" data-testid="admin-settings-error">{msg.error}</div>
        )}
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="overline text-[#8C939D] block mb-2">{label}</label>
      {children}
    </div>
  );
}

/* ---------- Gallery Tab ---------- */
function GalleryTab() {
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]); // [{name, dataUrl}]
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve({ name: file.name, dataUrl: r.result });
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    setError("");
    try {
      const read = await Promise.all(list.map(readFile));
      setFiles((prev) => [...prev, ...read]);
    } catch {
      setError("Could not read one or more files");
    }
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const upload = async (e) => {
    e.preventDefault();
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    setProgress({ done: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        await api.post("/admin/gallery", {
          title: title || f.name.replace(/\.[^.]+$/, ""),
          image: f.dataUrl,
        });
        setProgress({ done: i + 1, total: files.length });
      }
      setFiles([]);
      setTitle("");
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <form onSubmit={upload} className="lg:col-span-4 brut-card p-6 h-fit space-y-4" data-testid="admin-gallery-upload-form">
        <div className="overline">/// Add Photos · Multi-upload</div>
        <div>
          <label className="overline text-[#8C939D] block mb-2">Title prefix (optional)</label>
          <input className="input-brut" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Leave blank to use filenames" data-testid="admin-gallery-title-input" />
        </div>
        <div>
          <label className="overline text-[#8C939D] block mb-2">Images * (select multiple)</label>
          <label className="btn-outline text-xs w-full justify-center cursor-pointer" data-testid="admin-gallery-file-btn">
            <Upload size={14} /> {files.length > 0 ? `Add more (${files.length} selected)` : "Choose images"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
          </label>
        </div>

        {files.length > 0 && (
          <div className="grid grid-cols-3 gap-2" data-testid="admin-gallery-previews">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square overflow-hidden border border-[#282C33] group">
                <img src={f.dataUrl} alt={f.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 bg-[#DC2626] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`admin-gallery-preview-remove-${i}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploading && progress.total > 0 && (
          <div className="space-y-2" data-testid="admin-gallery-progress">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#8C939D]">
              Uploading {progress.done}/{progress.total}…
            </div>
            <div className="h-1 bg-[#282C33]">
              <div className="h-full bg-[#00A896] transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
            </div>
          </div>
        )}

        {error && <div className="border border-[#DC2626] text-[#FCA5A5] p-3 text-sm" data-testid="admin-gallery-error">{error}</div>}

        <button type="submit" disabled={files.length === 0 || uploading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-gallery-submit-btn">
          {uploading ? "Uploading…" : <><Upload size={16} /> Upload {files.length > 0 ? `${files.length} photo${files.length > 1 ? "s" : ""}` : ""}</>}
        </button>
      </form>

      <div className="lg:col-span-8" data-testid="admin-gallery-list">
        <div className="overline mb-4">/// Current Photos · {items.length}</div>
        {items.length === 0 ? (
          <div className="brut-card p-12 text-center text-[#8C939D] text-sm">No photos yet — upload one to get started.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((g) => (
              <div key={g.id} className="brut-card overflow-hidden group relative" data-testid={`admin-gallery-item-${g.id}`}>
                <div className="aspect-square">
                  <img src={g.image} alt={g.title} className="w-full h-full object-cover" />
                </div>
                {g.title && <div className="px-3 py-2 text-xs font-mono uppercase tracking-[0.15em] text-white border-t border-[#282C33] truncate">{g.title}</div>}
                <button onClick={() => remove(g.id)} className="absolute top-2 right-2 bg-[#DC2626] text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`admin-gallery-delete-${g.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Inquiries Tab ---------- */
function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/admin/inquiries/${id}`); await load(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-inquiries-list">
      <div className="overline mb-4">/// Quote Requests · {items.length}</div>
      {error && <div className="border border-[#DC2626] text-[#FCA5A5] p-3 text-sm mb-4">{error}</div>}
      {items.length === 0 ? (
        <div className="brut-card p-12 text-center text-[#8C939D] text-sm">No inquiries yet.</div>
      ) : (
        <div className="space-y-4">
          {items.map((i) => (
            <div key={i.id} className="brut-card p-6" data-testid={`admin-inquiry-${i.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-display font-bold text-xl text-white uppercase tracking-tight">{i.name}</div>
                  <div className="overline text-[#8C939D] mt-1">/// {new Date(i.created_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs font-mono">
                  <a href={`tel:${i.phone}`} className="text-[#00A896]">{i.phone}</a>
                  {i.email && <a href={`mailto:${i.email}`} className="text-[#8C939D] hover:text-[#00A896]">{i.email}</a>}
                </div>
              </div>
              {i.material && <div className="mt-3 text-xs font-mono uppercase tracking-[0.2em] text-[#00A896]">Material: {i.material}</div>}
              <p className="mt-4 text-[#C7CBD3] text-sm leading-relaxed whitespace-pre-wrap">{i.message}</p>
              <div className="mt-4 flex justify-end">
                <button onClick={() => remove(i.id)} className="text-xs font-mono uppercase tracking-[0.2em] text-[#DC2626] hover:underline flex items-center gap-1" data-testid={`admin-inquiry-delete-${i.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
